const RESIZE_DEBOUNCE_MS = 150;
const GUIDE_MAP_ID = 'oi-map';
const EARTH_RADIUS_MILES = 3958.8;
const READ_MORE_FALLBACK_LINES = 4;
const DEFAULT_PLACE_ICON = 'fa-light fa-location-dot';
const PLACE_TYPE_ICONS = {
    hospital: 'fa-light fa-hospital',
    doctor: 'fa-light fa-user-doctor',
    pharmacy: 'fa-light fa-prescription-bottle-medical',
    church: 'fa-light fa-church',
    grocery_or_supermarket: 'fa-light fa-basket-shopping',
    supermarket: 'fa-light fa-basket-shopping',
    restaurant: 'fa-light fa-fork-knife',
    cafe: 'fa-light fa-mug-hot',
    shopping_mall: 'fa-light fa-bag-shopping',
    park: 'fa-light fa-tree',
    school: 'fa-light fa-book-open',
    university: 'fa-light fa-graduation-cap',
    gym: 'fa-light fa-dumbbell',
    golf_course: 'fa-light fa-golf-flag-hole',
    library: 'fa-light fa-books',
};

function initReadMore() {
    const description = document.querySelector('[data-read-more]');
    const toggle = document.querySelector('[data-read-more-toggle]');
    if (!description || !toggle) return;

    function setExpanded(isExpanded) {
        description.classList.toggle('is-expanded', isExpanded);
        toggle.classList.toggle('collapsed', !isExpanded);
        toggle.setAttribute('aria-expanded', String(isExpanded));
        toggle.textContent = isExpanded ? 'Read Less' : 'Read More';
        if (!isExpanded) description.scrollTop = 0;
    }

    function measure() {
        description.classList.remove('is-truncated', 'is-expanded');
        description.style.removeProperty('--read-more-height');

        const firstBlock = description.firstElementChild;
        const top = description.getBoundingClientRect().top;
        const isContained = description.clientHeight < description.scrollHeight;
        let limit;
        if (firstBlock) {
            limit = firstBlock.getBoundingClientRect().bottom - top;
        } else if (isContained) {
            limit = description.clientHeight;
        } else {
            const styles = getComputedStyle(description);
            const lineHeight = parseFloat(styles.lineHeight) || parseFloat(styles.fontSize) * 1.5;
            limit = lineHeight * READ_MORE_FALLBACK_LINES;
        }
        if (isContained) {
            limit = Math.min(limit, description.clientHeight);
        }

        const isTruncated = description.scrollHeight > Math.ceil(limit) + 1;
        toggle.hidden = !isTruncated;
        if (!isTruncated) return;

        description.style.setProperty('--read-more-height', `${Math.floor(limit)}px`);
        description.classList.add('is-truncated');
        setExpanded(toggle.getAttribute('aria-expanded') === 'true');
    }

    toggle.addEventListener('click', () => {
        if (!description.classList.contains('is-truncated')) return;
        setExpanded(!description.classList.contains('is-expanded'));
    });

    let resizeTimer;
    window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(measure, RESIZE_DEBOUNCE_MS);
    });

    document.fonts.ready.then(measure);
}

function distanceInMiles(from, to) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(to.lat() - from.lat());
    const dLng = toRad(to.lng() - from.lng());
    const a = Math.sin(dLat / 2) ** 2
        + Math.cos(toRad(from.lat())) * Math.cos(toRad(to.lat())) * Math.sin(dLng / 2) ** 2;
    return 2 * EARTH_RADIUS_MILES * Math.asin(Math.sqrt(a));
}

function initCommunityGuide() {
    const accordion = document.getElementById('accordionNearby');
    if (!accordion) return;

    const resultLists = Array.from(accordion.querySelectorAll('[data-guide-results]'));
    const schoolsPanel = accordion.querySelector('[data-guide-schools]');
    const placesByType = {};
    let schoolMarkers = [];
    let renderQueued = false;

    function typesFor(element) {
        return element.dataset.search.split(',').map((type) => type.trim()).filter(Boolean);
    }

    function buildResult(entry, iconClass) {
        const item = document.createElement('li');
        item.className = 'list-group-item';

        const iconWrap = document.createElement('div');
        iconWrap.className = 'item-icon';
        const icon = document.createElement('i');
        icon.className = iconClass;
        icon.setAttribute('aria-hidden', 'true');
        iconWrap.appendChild(icon);

        const label = document.createElement('div');
        label.className = 'item-label';
        const heading = document.createElement('span');
        heading.className = 'item-heading';
        heading.textContent = entry.place.name || '';
        label.appendChild(heading);
        label.appendChild(document.createTextNode(entry.place.vicinity || ''));

        item.append(iconWrap, label);
        item.addEventListener('mouseenter', () => {
            window.google.maps.event.trigger(entry.marker, 'mouseover');
        });
        return item;
    }

    function render(map) {
        renderQueued = false;
        resultLists.forEach((list) => {
            const seen = new Set();
            const entries = [];
            typesFor(list).forEach((type) => {
                (placesByType[type] || []).forEach((entry) => {
                    const key = entry.place.place_id || entry.place.name;
                    if (seen.has(key)) return;
                    seen.add(key);
                    entries.push(entry);
                });
            });
            if (!entries.length) return;

            if (map.center) {
                entries.sort((a, b) => distanceInMiles(map.center, a.marker.getPosition())
                    - distanceInMiles(map.center, b.marker.getPosition()));
            }
            const iconType = typesFor(list).find((type) => PLACE_TYPE_ICONS[type]);
            const iconClass = PLACE_TYPE_ICONS[iconType] || DEFAULT_PLACE_ICON;
            list.replaceChildren(...entries.map((entry) => buildResult(entry, iconClass)));
        });
    }

    function clearSchoolMarkers() {
        schoolMarkers.forEach((marker) => marker.setMap(null));
        schoolMarkers = [];
    }

    function showSchoolMarkers(map) {
        clearSchoolMarkers();
        const bounds = new window.google.maps.LatLngBounds();
        if (map.center) bounds.extend(map.center);
        schoolsPanel.querySelectorAll('[data-guide-school]').forEach((school) => {
            const lat = parseFloat(school.dataset.latitude);
            const lng = parseFloat(school.dataset.longitude);
            if (Number.isNaN(lat) || Number.isNaN(lng)) return;
            const position = new window.google.maps.LatLng(lat, lng);
            schoolMarkers.push(new window.google.maps.Marker({
                map: map.map,
                position,
                title: school.dataset.name,
                icon: map.nearby_marker_image ? {
                    url: map.nearby_marker_image,
                    scaledSize: new window.google.maps.Size(37, 37),
                } : undefined,
            }));
            bounds.extend(position);
        });
        if (schoolMarkers.length) map.map.fitBounds(bounds);
    }

    function attach(map) {
        const createMarker = map.nearby_marker_create;
        map.nearby_marker_create = function (place, category) {
            createMarker.call(this, place, category);
            const markers = this.nearby_markers[category];
            placesByType[category] = placesByType[category] || [];
            placesByType[category].push({ place, marker: markers[markers.length - 1] });
            if (!renderQueued) {
                renderQueued = true;
                requestAnimationFrame(() => render(this));
            }
        };

        accordion.addEventListener('show.bs.collapse', (event) => {
            if (!map.map) return;
            const trigger = accordion.querySelector(`[data-bs-target="#${event.target.id}"]`);
            const isPlacesCategory = trigger && trigger.classList.contains('oi-map-nearby-filter');
            if (!isPlacesCategory) {
                accordion.querySelectorAll('.oi-map-nearby-filter.active').forEach((filter) => {
                    filter.classList.remove('active');
                });
                map.nearby_markers_clear();
            }
            if (event.target === schoolsPanel) {
                showSchoolMarkers(map);
            } else {
                clearSchoolMarkers();
            }
        });
    }

    function getVendor() {
        const loadedMap = window.oi && window.oi.loaded && window.oi.loaded.map[GUIDE_MAP_ID];
        return loadedMap ? loadedMap.vendor_implementation : null;
    }

    const vendor = getVendor();
    if (vendor) {
        attach(vendor);
    } else {
        document.addEventListener('oimap_idle', function attachOnIdle() {
            const idleVendor = getVendor();
            if (!idleVendor) return;
            document.removeEventListener('oimap_idle', attachOnIdle);
            attach(idleVendor);
        });
    }
}

function initSubnavSelect() {
    const list = document.getElementById('detail-subnav');
    const select = document.getElementById('detail-subnav-select');
    if (!list || !select) return;

    const hashes = [];
    list.querySelectorAll('li a').forEach((link) => {
        const option = document.createElement('option');
        option.value = link.getAttribute('href');
        option.textContent = link.textContent.trim();
        select.appendChild(option);
        hashes.push(link.getAttribute('href'));
    });

    select.addEventListener('focus', () => {
        const y = window.scrollY;
        requestAnimationFrame(() => window.scrollTo(0, y));
    });

    select.addEventListener('change', () => {
        window.location.href = select.value;
    });

    const sections = hashes.map((hash) => document.querySelector(hash)).filter(Boolean);
    if (!sections.length) return;

    const visible = new Set();
    const scrollPaddingTop = parseInt(getComputedStyle(document.documentElement).scrollPaddingTop, 10) || 48;
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                visible.add(entry.target);
            } else {
                visible.delete(entry.target);
            }
        });
        const active = sections.find((section) => visible.has(section));
        if (active) {
            select.value = `#${active.id}`;
        }
    }, {
        rootMargin: `-${scrollPaddingTop}px 0px -50% 0px`,
    });

    sections.forEach((section) => observer.observe(section));
}

initSubnavSelect();
initReadMore();
initCommunityGuide();
